import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import {
  Form, Input, Button, Select, DatePicker, Radio, Checkbox, message, Progress, Upload, Spin,
} from "antd";
import {
  ArrowLeftOutlined, CheckOutlined, UploadOutlined, FileTextOutlined, DeleteOutlined,
} from "@ant-design/icons";
import moment from "moment";
import ContractorPageWrapper from "./Contractor.style";
import RegisterTab from "./RegisterTab";
import { contractorService } from "../../../services/contractor.service";
import { registerServices } from "../../../services/contractorRegister.service";
import { attachment } from "../../../services/attachment.service";
import { userService } from "../../../services/user.service";
import { commonHelper } from "@iso/lib/helpers/commonHelper";
import {
  CONTRACTOR_TYPES,
  CONTRACTOR_CATEGORIES,
  ENGAGEMENT_TYPES,
  HAZARD_FLAGS,
  SUPERVISION_LEVELS,
  APPROVAL_AUTHORITIES,
  CONTRACTOR_RISK_META,
} from "@iso/constants/contractor";
import { REGISTER_CONFIGS } from "@iso/constants/contractorRegisters";

const { TextArea } = Input;

const STEPS = [
  { key: "business", title: "Business Details", desc: "Entity, ABN, category & address" },
  { key: "contact", title: "Contact Details", desc: "Primary contact & emergency" },
  { key: "contract", title: "Contract & Scope", desc: "Engagement, dates & locations" },
  { key: "risk", title: "Risk Classification", desc: "Hazards & required supervision" },
  { key: "compliance", title: "Compliance Records", desc: "Optional — insurance, workers, assets & docs" },
  { key: "review", title: "Review & Submit", desc: "Confirm and create profile" },
];

/**
 * Registers offered inside the wizard, in dependency order: workers have
 * to exist before a licence or induction can name a holder. These are the
 * same configs and the same RegisterTab the profile renders, against the
 * same child endpoints — a record added here IS the record the profile
 * shows, not a staged copy replayed on submit.
 */
const WIZARD_REGISTERS = [
  { key: "personnel", label: "Personnel" },
  { key: "insurance", label: "Insurance" },
  { key: "assets", label: "Assets" },
  { key: "licences", label: "Licences" },
  { key: "inductions", label: "Inductions" },
  { key: "safety", label: "Safety" },
];

/** Review-step wording for a register the user left empty. */
const REGISTER_NEXT_STEP = {
  insurance: "Add insurance policies in the Insurance tab",
  personnel: "Register workers in the Personnel tab",
  assets: "Add plant and equipment in the Assets tab",
  licences: "Upload licences and competency evidence in the Licences tab",
  inductions: "Record site inductions in the Inductions tab",
  safety: "Upload safety documents and SWMS in the Safety tab",
};

/**
 * Client-side ABN checksum (same ABR algorithm the backend enforces):
 * gives the "Verify ABN" button instant feedback without a round-trip.
 * A real ABR web-service lookup (name/GST auto-fill) needs an ABR GUID —
 * follow-up once one is provisioned; until then abn_verified stays false
 * server-side and the UI says "format valid".
 */
const abnChecksumValid = (abn) => {
  const digits = (abn || "").replace(/\D/g, "");
  if (digits.length !== 11) return false;
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const sum = digits
    .split("")
    .reduce((acc, d, i) => acc + (Number(d) - (i === 0 ? 1 : 0)) * weights[i], 0);
  return sum % 89 === 0;
};

const AddContractorWizard = () => {
  const history = useHistory();
  const [form] = Form.useForm();
  // Present on /contractors/:id/edit — same wizard, edit mode: loads the
  // profile, prefills every step, and submits an update instead of a
  // create. This is how a pending (bridged) contractor gets built out
  // before approval.
  const { id: editId } = useParams();
  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  const activeProperty = commonHelper.getActiveProperty();
  // The registers are owner/admin-gated server-side exactly as they are
  // on the profile; mirror that here so the Add buttons match the API.
  const activePermission = useSelector(
    (state) => state.permission.activePermission
  );
  const canManage =
    activePermission === "owner" || activePermission === "admin";

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [stepSaving, setStepSaving] = useState(false);
  // Set when create-mode's first Continue persists the draft; from then
  // on every step is an update against this id.
  const [createdId, setCreatedId] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(!!editId);
  // Wizard state accumulates across steps; each step's form validates
  // only its own fields before merging into `data`.
  const [data, setData] = useState({});
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [abnStatus, setAbnStatus] = useState(null); // null | 'valid' | 'invalid'
  const [riskLevel, setRiskLevel] = useState(null);
  const [hazards, setHazards] = useState([]);
  // { id, name, url } of the uploaded contract / service agreement.
  const [contractDoc, setContractDoc] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  // Compliance step: the register showing, the worker pool its Licences
  // and Inductions holder selectors read from, and a per-register row
  // count driving the tab pills and the review step's outstanding list.
  const [complianceTab, setComplianceTab] = useState("personnel");
  const [workers, setWorkers] = useState([]);
  const [registerCounts, setRegisterCounts] = useState(null);

  useEffect(() => {
    if (activePropertyId) {
      // Corporate-scoped options, computed server-side: all properties
      // in the current property's corporate group, or just the current
      // property when it isn't part of one. NOT the user's membership
      // list — that leaked unrelated properties into the checklist.
      contractorService
        .getWorkLocationOptions(activePropertyId)
        .then((res) => {
          if (res?.code === "0000") setProperties(res.properties || []);
        })
        .catch((err) => console.error("Failed to load work locations:", err));
      userService.getUserForProperty(activePropertyId).then((res) => {
        if (res && res.code === "0000") setUsers(res.users || []);
      });
    }
  }, [activePropertyId]);

  useEffect(() => {
    if (!editId) return;
    contractorService
      .getOne(editId)
      .then((res) => {
        if (res?.code === "0000" && res.contractor) {
          const c = res.contractor;
          setData({
            ...c,
            contract_commencement: c.contract_commencement ? moment(c.contract_commencement) : null,
            contract_expiry: c.contract_expiry ? moment(c.contract_expiry) : null,
            next_review_date: c.next_review_date ? moment(c.next_review_date) : null,
          });
          setRiskLevel(c.risk_level || null);
          setHazards(c.hazard_flags || []);
          if (c.contract_attachment) {
            setContractDoc({
              id: c.contract_attachment.id,
              name: c.contract_attachment.display_name || "Contract document",
              url: c.contract_attachment.full_path,
            });
          }
        } else {
          message.error(res?.message || "Contractor not found");
          history.push("/contractors");
        }
      })
      .catch((err) => {
        console.error("Failed to load contractor for edit:", err);
        message.error("Failed to load contractor");
        history.push("/contractors");
      })
      .finally(() => setLoadingExisting(false));
  }, [editId]);

  // Each step mounts its own Form.Items, so re-seed the form from the
  // accumulated data whenever the visible step (or loaded record)
  // changes. Extra keys are ignored by antd.
  useEffect(() => {
    form.setFieldsValue(data);
  }, [step, data, loadingExisting]);

  const progress = Math.round((step / (STEPS.length - 1)) * 100);

  const handleVerifyAbn = () => {
    const abn = form.getFieldValue("abn");
    setAbnStatus(abnChecksumValid(abn) ? "valid" : "invalid");
  };

  /**
   * Serialise one step's validated values into a partial API payload.
   * Dates become Y-m-d strings; the non-Form state (risk level, hazard
   * flags, contract document) rides along with the step that owns it.
   */
  const stepPayload = (values) => {
    const payload = { ...values };
    ["contract_commencement", "contract_expiry", "next_review_date"].forEach((k) => {
      if (payload[k] !== undefined) {
        payload[k] = payload[k] ? moment(payload[k]).format("YYYY-MM-DD") : null;
      }
    });
    if (STEPS[step].key === "contract") {
      payload.contract_attachment_id = contractDoc?.id ?? null;
    }
    if (STEPS[step].key === "risk") {
      payload.risk_level = riskLevel;
      payload.hazard_flags = hazards;
    }
    return payload;
  };

  const recordId = editId || createdId;

  const fetchWorkers = () => {
    if (!recordId) return;
    registerServices["contractor-worker"]
      .list(recordId)
      .then((res) => {
        if (res?.code === "0000") setWorkers(res.items || []);
      })
      .catch((err) => console.error("Failed to load workers:", err));
  };

  /**
   * Row count per register. The review step needs all six at once to say
   * what is still outstanding, and only the visible register knows its
   * own rows, so this asks each endpoint directly rather than inferring
   * from whichever tab happens to be mounted. A register that fails to
   * load counts as empty — the review callout then over-reports work to
   * do, which is the safe direction to be wrong in.
   */
  const fetchRegisterCounts = () => {
    if (!recordId) return;
    Promise.all(
      WIZARD_REGISTERS.map((r) =>
        registerServices[REGISTER_CONFIGS[r.key].endpoint]
          .list(recordId)
          .then((res) => [r.key, res?.code === "0000" ? (res.items || []).length : 0])
          .catch((err) => {
            console.error(`Failed to count ${r.key} records:`, err);
            return [r.key, 0];
          })
      )
    ).then((pairs) => {
      const counts = {};
      pairs.forEach(([k, n]) => { counts[k] = n; });
      setRegisterCounts(counts);
    });
  };

  // The registers hang off a contractor row, which exists from step 1's
  // draft onward (or immediately in edit mode). Load what each step needs
  // on arrival rather than up front, so a user who never opens the
  // compliance step never pays for these requests.
  useEffect(() => {
    if (!recordId) return;
    const key = STEPS[step].key;
    if (key === "compliance") {
      fetchWorkers();
      fetchRegisterCounts();
    } else if (key === "review") {
      fetchRegisterCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, recordId]);

  /**
   * A register write inside the wizard moves the tab counts, and a
   * Personnel write also changes the worker pool the Licences and
   * Inductions holder selectors are built from.
   */
  const handleRegisterChanged = (registerKey) => {
    fetchRegisterCounts();
    if (registerKey === "personnel") fetchWorkers();
  };

  // Every Continue persists the current step server-side (per product:
  // "confirm saving at every section / continue stage" — progress must
  // survive the user leaving mid-wizard). Create mode persists a draft
  // (information_incomplete) on the first Continue and updates it from
  // then on. On save failure the wizard does NOT advance — moving on
  // would silently break the persistence promise.
  const next = () => {
    // Nothing on the compliance step belongs to the contractor row —
    // each register persists its own record the moment it is added — so
    // there is no partial payload to save on the way out.
    if (STEPS[step].key === "compliance") {
      setStep((s) => s + 1);
      return;
    }
    form
      .validateFields()
      .then((values) => {
        setStepSaving(true);
        const payload = stepPayload(values);
        const request = recordId
          ? contractorService.update(recordId, payload)
          : contractorService.create({
              ...payload,
              property_id: activePropertyId,
              _draft: true,
            });
        request
          .then((res) => {
            if (res?.code === "0000") {
              if (!recordId && res.contractor?.id) {
                setCreatedId(res.contractor.id);
              }
              setData((d) => ({ ...d, ...values }));
              setStep((s) => s + 1);
              message.success({ content: "Progress saved", duration: 1.2 });
            } else {
              message.error(res?.message || "Failed to save progress");
            }
          })
          .catch((err) => {
            console.error("Step save failed:", err);
            message.error("Failed to save progress");
          })
          .finally(() => setStepSaving(false));
      })
      .catch((info) => {
        // Validation rejection is expected UX — antd renders the errors
        // inline on the offending fields and scrollToFirstError takes the
        // user there. Nothing to toast; log for debuggability only.
        console.debug("Wizard step validation failed:", info);
      });
  };

  const back = () => {
    // Persist current (possibly partial) input so Back doesn't lose it.
    setData((d) => ({ ...d, ...form.getFieldsValue() }));
    setStep((s) => s - 1);
  };

  // The wizard's editable surface, and nothing else. In edit mode `data`
  // holds the entire record returned by the server — spreading it back
  // into the payload would round-trip server-computed fields (id,
  // status, timestamps, expansions) and silently depend on backend
  // stripping. A whitelist keeps the payload lean and the contract
  // explicit.
  const WIZARD_FIELDS = [
    "business_name", "trading_name", "contractor_type", "category",
    "abn", "acn", "gst_registered", "business_address", "postal_address",
    "website", "service_area",
    "primary_contact_name", "primary_contact_role", "primary_contact_phone",
    "primary_contact_email", "after_hours_phone",
    "emergency_contact_name", "emergency_contact_phone", "internal_owner_id",
    "engagement_type", "scope_description", "approved_property_ids",
    "subcontracting_approved",
    "supervision_level", "approval_authority", "internal_notes",
  ];

  const submit = () => {
    setSaving(true);
    const payload = {};
    WIZARD_FIELDS.forEach((k) => {
      if (data[k] !== undefined) payload[k] = data[k];
    });
    payload.property_id = activePropertyId;
    payload.risk_level = riskLevel;
    payload.hazard_flags = hazards;
    payload.contract_attachment_id = contractDoc?.id ?? null;
    payload.contract_commencement = data.contract_commencement
      ? moment(data.contract_commencement).format("YYYY-MM-DD")
      : null;
    payload.contract_expiry = data.contract_expiry
      ? moment(data.contract_expiry).format("YYYY-MM-DD")
      : null;
    payload.next_review_date = data.next_review_date
      ? moment(data.next_review_date).format("YYYY-MM-DD")
      : null;
    // Per-step saving means a record always exists by the review step
    // (editId in edit mode, createdId from step 1's draft in create
    // mode). One atomic finalize: the server promotes an
    // information_incomplete profile to pending_review in the same save,
    // so there's no update-then-changeStatus split-brain, and a draft
    // reopened later via edit still gets promoted (it keys off the row's
    // current status, not this session). An already-approved profile is
    // left untouched.
    payload._finalize = true;

    contractorService
      .update(recordId, payload)
      .then((res) => {
        if (res?.code === "0000") {
          message.success(editId ? "Contractor profile updated" : "Contractor profile created");
          history.push(`/contractors/${recordId}`);
        } else {
          message.error(res?.message || "Failed to save contractor");
          setSaving(false);
        }
      })
      .catch((err) => {
        console.error("Failed to save contractor:", err);
        message.error("Failed to save contractor");
        setSaving(false);
      });
  };

  const handleContractUpload = ({ file, onSuccess, onError }) => {
    setUploadingDoc(true);
    attachment
      .uploadFile(file, "file")
      .then((res) => {
        const att = res?.attachment;
        if (att?.id) {
          setContractDoc({
            id: att.id,
            name: att.display_name || file.name,
            url: att.full_path,
          });
          onSuccess(res);
        } else {
          message.error("Upload failed — no attachment returned");
          onError(new Error("no attachment"));
        }
      })
      .catch((err) => {
        console.error("Contract upload failed:", err);
        message.error("Failed to upload document");
        onError(err);
      })
      .finally(() => setUploadingDoc(false));
  };

  /* ------------------------------ steps ------------------------------ */

  const renderBusinessDetails = () => (
    <>
      <div className="info-callout">
        The ABN is checked against the official checksum as you verify.
        Full Australian Business Register lookup (auto-filling entity
        structure and GST status) is a planned follow-up.
      </div>
      <div className="wizard-card">
        <div className="card-heading">Business Identity</div>
        <Form.Item
          name="business_name"
          label="Contractor Business Name"
          rules={[{ required: true, whitespace: true, message: "Business name is required" }]}
        >
          <Input size="large" placeholder="e.g. TopGun Electrical Services Pty Ltd" />
        </Form.Item>
        <Form.Item
          name="trading_name"
          label="Trading Name"
          extra="If the business trades under a different name to its registered legal name."
        >
          <Input size="large" placeholder="e.g. TopGun Electrical" />
        </Form.Item>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Form.Item
            name="contractor_type"
            label="Contractor Type"
            rules={[{ required: true, message: "Select a type" }]}
          >
            <Select size="large" placeholder="Select...">
              {CONTRACTOR_TYPES.map((t) => (
                <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="category"
            label="Contractor Category"
            rules={[{ required: true, message: "Select a category" }]}
          >
            <Select size="large" placeholder="Select category..." showSearch>
              {CONTRACTOR_CATEGORIES.map((c) => (
                <Select.Option key={c} value={c}>{c}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </div>

      <div className="wizard-card">
        <div className="card-heading">ABN &amp; Tax Details</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 12, alignItems: "end" }}>
          <Form.Item
            name="abn"
            label="ABN"
            extra="11 digits, no spaces."
            rules={[
              { required: true, message: "ABN is required" },
              {
                validator: (_, v) =>
                  !v || abnChecksumValid(v)
                    ? Promise.resolve()
                    : Promise.reject(new Error("ABN failed checksum validation")),
              },
            ]}
          >
            <Input size="large" placeholder="XX XXX XXX XXX" onChange={() => setAbnStatus(null)} />
          </Form.Item>
          <Form.Item label=" " colon={false}>
            <Button size="large" onClick={handleVerifyAbn}>Verify ABN</Button>
          </Form.Item>
          <Form.Item
            name="acn"
            label="ACN"
            extra="Required where the entity is a company."
          >
            <Input size="large" placeholder="XXX XXX XXX" />
          </Form.Item>
        </div>
        {abnStatus === "valid" && (
          <div style={{ color: "#067647", fontSize: 13 }}>
            <CheckOutlined /> ABN format valid (checksum passed)
          </div>
        )}
        {abnStatus === "invalid" && (
          <div style={{ color: "#b42318", fontSize: 13 }}>
            ABN failed checksum — re-check the number.
          </div>
        )}
        <Form.Item
          name="gst_registered"
          label="GST Registered"
          extra="Is the business registered for GST?"
          initialValue={false}
        >
          <Radio.Group size="large">
            <Radio.Button value={true}>Yes</Radio.Button>
            <Radio.Button value={false}>No</Radio.Button>
          </Radio.Group>
        </Form.Item>
      </div>

      <div className="wizard-card">
        <div className="card-heading">Address &amp; Location</div>
        <Form.Item
          name="business_address"
          label="Business / Registered Address"
          rules={[{ required: true, whitespace: true, message: "Address is required" }]}
        >
          <Input size="large" placeholder="Street, Suburb, State, Postcode" />
        </Form.Item>
        <Form.Item
          name="postal_address"
          label="Postal Address"
          extra="If different from the registered address."
        >
          <Input size="large" placeholder="PO Box or street address" />
        </Form.Item>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Form.Item name="website" label="Website" extra="Optional.">
            <Input size="large" placeholder="www.example.com.au" />
          </Form.Item>
          <Form.Item
            name="service_area"
            label="Geographic Service Area"
            extra="Regions the contractor normally services."
          >
            <Input size="large" placeholder="e.g. Darling Downs, South West QLD" />
          </Form.Item>
        </div>
      </div>
    </>
  );

  const renderContactDetails = () => (
    <>
      <div className="wizard-card">
        <div className="card-heading">Primary Contact</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Form.Item
            name="primary_contact_name"
            label="Full Name"
            rules={[{ required: true, whitespace: true, message: "Contact name is required" }]}
          >
            <Input size="large" placeholder="e.g. Jake Morrison" />
          </Form.Item>
          <Form.Item
            name="primary_contact_role"
            label="Role"
            extra="Their role within the contractor organisation."
            rules={[{ required: true, whitespace: true, message: "Role is required" }]}
          >
            <Input size="large" placeholder="e.g. Operations Manager" />
          </Form.Item>
          <Form.Item
            name="primary_contact_phone"
            label="Phone Number"
            rules={[{ required: true, message: "Phone number is required" }]}
          >
            <Input size="large" placeholder="07 XXXX XXXX or 04XX XXX XXX" />
          </Form.Item>
          <Form.Item
            name="primary_contact_email"
            label="Email Address"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Not a valid email" },
            ]}
          >
            <Input size="large" placeholder="contact@company.com.au" />
          </Form.Item>
        </div>
        <Form.Item
          name="after_hours_phone"
          label="After-Hours Contact"
          extra="Required for higher-risk contractors — a direct number reachable outside business hours."
        >
          <Input size="large" placeholder="04XX XXX XXX" />
        </Form.Item>
      </div>

      <div className="wizard-card">
        <div className="card-heading">Emergency Contact</div>
        <div style={{ fontSize: 13, color: "#667085", marginBottom: 12 }}>
          A person who can be reached in an emergency — must not be the same
          person as the primary contact.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Form.Item
            name="emergency_contact_name"
            label="Emergency Contact Name"
            rules={[
              { required: true, whitespace: true, message: "Emergency contact is required" },
              {
                validator: (_, v) =>
                  !v ||
                  v.trim().toLowerCase() !==
                    (form.getFieldValue("primary_contact_name") || data.primary_contact_name || "")
                      .trim()
                      .toLowerCase()
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Emergency contact must differ from the primary contact")
                      ),
              },
            ]}
          >
            <Input size="large" placeholder="Full name" />
          </Form.Item>
          <Form.Item
            name="emergency_contact_phone"
            label="Emergency Contact Phone"
            rules={[{ required: true, message: "Emergency phone is required" }]}
          >
            <Input size="large" placeholder="04XX XXX XXX" />
          </Form.Item>
        </div>
      </div>

      <div className="wizard-card">
        <div className="card-heading">Internal Ownership</div>
        <Form.Item
          name="internal_owner_id"
          label="Internal Contractor Owner"
          extra="Receives review reminders and compliance alerts for this contractor."
          rules={[{ required: true, message: "Select the responsible team member" }]}
        >
          <Select size="large" placeholder="Select farm team member..." showSearch optionFilterProp="children">
            {users.map((u) => (
              <Select.Option key={u.id} value={u.id}>{u.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>
    </>
  );

  const renderContractScope = () => (
    <>
      <div className="wizard-card">
        <div className="card-heading">Engagement Details</div>
        <Form.Item
          name="engagement_type"
          label="Engagement Type"
          rules={[{ required: true, message: "Select a type" }]}
        >
          <Select size="large" placeholder="Select type...">
            {ENGAGEMENT_TYPES.map((t) => (
              <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Form.Item
            name="contract_commencement"
            label="Contract Commencement Date"
            rules={[{ required: true, message: "Commencement date is required" }]}
          >
            <DatePicker size="large" style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="contract_expiry"
            label="Contract Expiry Date"
            extra="Leave blank for open-ended agreements — a review date will be required instead."
          >
            <DatePicker size="large" style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
        </div>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) =>
            !getFieldValue("contract_expiry") ? (
              <Form.Item
                name="next_review_date"
                label="Next Review Date"
                extra="Required for open-ended agreements so compliance is re-checked."
                rules={[{ required: true, message: "Review date required for open-ended agreements" }]}
              >
                <DatePicker size="large" style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            ) : null
          }
        </Form.Item>
      </div>

      <div className="wizard-card">
        <div className="card-heading">Scope of Services</div>
        <Form.Item
          name="scope_description"
          label="Scope Description"
          extra="Be specific. Approved activities on work orders must fall within this scope."
          rules={[{ required: true, whitespace: true, message: "Scope description is required" }]}
        >
          <TextArea
            rows={4}
            placeholder="e.g. Electrical installation, maintenance, fault-finding and certification across all properties including irrigation pump controls and solar/battery systems."
          />
        </Form.Item>
      </div>

      <div className="wizard-card">
        <div className="card-heading">Approved Work Locations</div>
        <div style={{ fontSize: 13, color: "#667085", marginBottom: 12 }}>
          Properties this contractor is approved to access.
        </div>
        <Form.Item name="approved_property_ids" initialValue={activePropertyId ? [activePropertyId] : []}>
          <Checkbox.Group style={{ width: "100%" }}>
            <div className="property-grid">
              {properties.map((p) => (
                <Checkbox key={p.id} value={p.id}>{p.name}</Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        </Form.Item>
      </div>

      <div className="wizard-card">
        <div className="card-heading">Subcontracting</div>
        <Form.Item
          name="subcontracting_approved"
          label="Subcontracting Approved"
          extra="Can this contractor engage subcontractors to perform work on this property? If yes, subcontractor details must be provided and approved before work commences."
          initialValue={false}
        >
          <Radio.Group size="large">
            <Radio.Button value={true}>Yes</Radio.Button>
            <Radio.Button value={false}>No</Radio.Button>
          </Radio.Group>
        </Form.Item>
      </div>

      <div className="wizard-card">
        <div className="card-heading">Contract Document</div>
        <div style={{ fontSize: 13, color: "#667085", marginBottom: 12 }}>
          Upload a signed copy of the contract or service agreement — PDF or Word, max 20 MB.
        </div>
        {contractDoc ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <FileTextOutlined style={{ fontSize: 18, color: "#0b2a1b" }} />
            {contractDoc.url ? (
              <a href={contractDoc.url} target="_blank" rel="noopener noreferrer">
                {contractDoc.name}
              </a>
            ) : (
              <span>{contractDoc.name}</span>
            )}
            <Button
              size="small"
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => setContractDoc(null)}
            >
              Remove
            </Button>
          </div>
        ) : (
          <Upload.Dragger
            customRequest={handleContractUpload}
            showUploadList={false}
            multiple={false}
            accept=".pdf,.doc,.docx"
            beforeUpload={(file) => {
              if (file.size / 1024 / 1024 > 20) {
                message.error("File must be smaller than 20 MB");
                return Upload.LIST_IGNORE;
              }
              return true;
            }}
          >
            {uploadingDoc ? (
              <Spin />
            ) : (
              <>
                <p><UploadOutlined style={{ fontSize: 22, color: "#667085" }} /></p>
                <p style={{ color: "#344054" }}>Drop file here or click to browse</p>
              </>
            )}
          </Upload.Dragger>
        )}
      </div>
    </>
  );

  const renderRiskClassification = () => (
    <>
      <div className="info-callout">
        Risk classification determines required documentation, supervision
        level, and the approval authority needed before work commences. It
        is automatically reviewed when work type or hazards change.
      </div>
      <div className="wizard-card">
        <div className="card-heading">Risk Level</div>
        <div className="risk-options">
          {Object.entries(CONTRACTOR_RISK_META).map(([value, meta]) => (
            <div
              key={value}
              className={`risk-option ${meta.className} ${riskLevel === value ? "selected" : ""}`}
              onClick={() => setRiskLevel(value)}
            >
              <div className="risk-name">{meta.label}</div>
              <div className="risk-desc">{meta.description}</div>
            </div>
          ))}
        </div>
        {!riskLevel && (
          <div style={{ color: "#b42318", fontSize: 13, marginTop: 8 }}>
            Select the overall risk level for this contractor's work.
          </div>
        )}
      </div>

      <div className="wizard-card">
        <div className="card-heading">Hazard Flags</div>
        <div style={{ fontSize: 13, color: "#667085", marginBottom: 12 }}>
          Select all hazards that apply to this contractor's work.
        </div>
        <div className="hazard-grid">
          {HAZARD_FLAGS.map((h) => {
            const checked = hazards.includes(h.key);
            return (
              <div
                key={h.key}
                className={`hazard-item ${checked ? "checked" : ""}`}
                onClick={() =>
                  setHazards((prev) =>
                    checked ? prev.filter((k) => k !== h.key) : [...prev, h.key]
                  )
                }
              >
                <Checkbox checked={checked} />
                {h.label}
              </div>
            );
          })}
        </div>
      </div>

      <div className="wizard-card">
        <div className="card-heading">Supervision &amp; Approval</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Form.Item
            name="supervision_level"
            label="Required Supervision Level"
            extra="Level of farm oversight required during work."
            rules={[{ required: true, message: "Select a level" }]}
          >
            <Select size="large" placeholder="Select level...">
              {SUPERVISION_LEVELS.map((s) => (
                <Select.Option key={s.value} value={s.value}>{s.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="approval_authority"
            label="Final Approval Authority"
            extra="Who must sign off before work can commence."
            rules={[{ required: true, message: "Select an authority" }]}
          >
            <Select size="large" placeholder="Select...">
              {APPROVAL_AUTHORITIES.map((a) => (
                <Select.Option key={a.value} value={a.value}>{a.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </div>

      <div className="wizard-card">
        <div className="card-heading">Internal Notes (Restricted)</div>
        <div style={{ fontSize: 13, color: "#667085", marginBottom: 12 }}>
          These notes are visible only to farm management and are not shared
          with the contractor.
        </div>
        <Form.Item name="internal_notes" label="Notes">
          <TextArea
            rows={3}
            placeholder="e.g. Only to be engaged for specific pump maintenance — refer all scope changes to farm manager before authorising."
          />
        </Form.Item>
      </div>
    </>
  );

  const reviewValue = (v, formatter) => {
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) {
      return <div className="review-value missing">Not provided</div>;
    }
    return <div className="review-value">{formatter ? formatter(v) : String(v)}</div>;
  };

  const renderReview = () => {
    const d = { ...data, ...form.getFieldsValue() };
    const propertyNames = (d.approved_property_ids || [])
      .map((id) => properties.find((p) => p.id === id)?.name)
      .filter(Boolean)
      .join(", ");
    const ownerName = users.find((u) => u.id === d.internal_owner_id)?.name;
    return (
      <>
        <div className="info-callout">
          {editId ? (
            <>Review the changes before saving. The profile keeps its
            current status — approval stays a separate action on the
            profile page.</>
          ) : (
            <>Review all details before creating the contractor profile.
            The profile is created with status <strong>Pending Review</strong>.
            Any compliance records you skipped can still be added at any
            time from the profile tabs.</>
          )}
        </div>
        <div className="wizard-card">
          <div className="card-heading">Business Details</div>
          <div className="review-grid">
            <div><div className="review-label">Business Name</div>{reviewValue(d.business_name)}</div>
            <div><div className="review-label">Trading Name</div>{reviewValue(d.trading_name)}</div>
            <div><div className="review-label">Contractor Type</div>{reviewValue(CONTRACTOR_TYPES.find((t) => t.value === d.contractor_type)?.label)}</div>
            <div><div className="review-label">Category</div>{reviewValue(d.category)}</div>
            <div><div className="review-label">ABN</div>{reviewValue(d.abn, (v) => `${v}${abnChecksumValid(v) ? " ✓ checksum ok" : ""}`)}</div>
            <div><div className="review-label">GST Registered</div>{reviewValue(d.gst_registered ? "Yes" : "No")}</div>
            <div><div className="review-label">Address</div>{reviewValue(d.business_address)}</div>
            <div><div className="review-label">Service Area</div>{reviewValue(d.service_area)}</div>
          </div>
        </div>
        <div className="wizard-card">
          <div className="card-heading">Contact Details</div>
          <div className="review-grid">
            <div><div className="review-label">Primary Contact</div>{reviewValue(d.primary_contact_name)}</div>
            <div><div className="review-label">Role</div>{reviewValue(d.primary_contact_role)}</div>
            <div><div className="review-label">Phone</div>{reviewValue(d.primary_contact_phone)}</div>
            <div><div className="review-label">Email</div>{reviewValue(d.primary_contact_email)}</div>
            <div><div className="review-label">After-Hours</div>{reviewValue(d.after_hours_phone)}</div>
            <div><div className="review-label">Emergency Contact</div>{reviewValue(d.emergency_contact_name)}</div>
            <div><div className="review-label">Internal Owner</div>{reviewValue(ownerName)}</div>
          </div>
        </div>
        <div className="wizard-card">
          <div className="card-heading">Contract &amp; Scope</div>
          <div className="review-grid">
            <div><div className="review-label">Engagement Type</div>{reviewValue(ENGAGEMENT_TYPES.find((t) => t.value === d.engagement_type)?.label)}</div>
            <div><div className="review-label">Commencement</div>{reviewValue(d.contract_commencement, (v) => moment(v).format("DD MMM YYYY"))}</div>
            <div><div className="review-label">Expiry</div>{reviewValue(d.contract_expiry, (v) => moment(v).format("DD MMM YYYY"))}</div>
            <div><div className="review-label">Subcontracting</div>{reviewValue(d.subcontracting_approved ? "Approved" : "Not approved")}</div>
            <div><div className="review-label">Approved Locations</div>{reviewValue(propertyNames)}</div>
            <div><div className="review-label">Scope</div>{reviewValue(d.scope_description)}</div>
          </div>
        </div>
        <div className="wizard-card">
          <div className="card-heading">Risk Classification</div>
          <div className="review-grid">
            <div><div className="review-label">Risk Level</div>{reviewValue(CONTRACTOR_RISK_META[riskLevel]?.label)}</div>
            <div><div className="review-label">Hazards</div>{reviewValue(hazards.map((k) => HAZARD_FLAGS.find((h) => h.key === k)?.label).filter(Boolean).join(", "))}</div>
            <div><div className="review-label">Supervision</div>{reviewValue(SUPERVISION_LEVELS.find((s) => s.value === d.supervision_level)?.label)}</div>
            <div><div className="review-label">Approval Authority</div>{reviewValue(APPROVAL_AUTHORITIES.find((a) => a.value === d.approval_authority)?.label)}</div>
          </div>
        </div>
        {renderNextSteps()}
      </>
    );
  };

  /**
   * Only the registers still empty are worth listing — the old callout
   * named all six unconditionally, which read as a to-do list even when
   * the user had just filled them in on the compliance step.
   */
  const renderNextSteps = () => {
    const outstanding = registerCounts
      ? WIZARD_REGISTERS.filter((r) => !registerCounts[r.key])
      : null;
    const approvalStep = (
      <li>Complete compliance review and change status to Approved</li>
    );
    if (outstanding === null) {
      return (
        <div className="next-steps-callout">
          <strong>Next steps after creating the profile:</strong>
          <ul>{approvalStep}</ul>
        </div>
      );
    }
    if (outstanding.length === 0) {
      return (
        <div className="next-steps-callout">
          <strong>Every compliance register has at least one record.</strong>
          <ul>{approvalStep}</ul>
        </div>
      );
    }
    return (
      <div className="next-steps-callout">
        <strong>Still to add after creating the profile:</strong>
        <ul>
          {outstanding.map((r) => (
            <li key={r.key}>{REGISTER_NEXT_STEP[r.key]}</li>
          ))}
          {approvalStep}
        </ul>
      </div>
    );
  };

  /**
   * Compliance registers, inline. The wizard already persists a real
   * contractor row on the first Continue, and the child endpoints gate on
   * "contractor exists + caller manages its property" with no status
   * check, so a draft can carry records before it is ever submitted.
   */
  const renderComplianceRecords = () => {
    if (!recordId) {
      return (
        <div className="info-callout">
          Finish the earlier steps first — they create the draft profile
          these records attach to.
        </div>
      );
    }
    return (
      <>
        <div className="info-callout">
          Optional. Anything added here saves against the contractor
          immediately and appears on the profile tabs afterwards, so you can
          press <strong>Continue</strong> to skip any of it. Register people
          under <strong>Personnel</strong> first if you want to name a holder
          on their licences and inductions.
        </div>
        <div className="profile-tabs">
          {WIZARD_REGISTERS.map((r) => (
            <button
              key={r.key}
              type="button"
              className={`ctr-tab ${complianceTab === r.key ? "active" : ""}`}
              onClick={() => setComplianceTab(r.key)}
            >
              {r.label}
              {registerCounts?.[r.key] > 0 && (
                <span className={`tab-count ${complianceTab === r.key ? "active" : ""}`}>
                  {registerCounts[r.key]}
                </span>
              )}
            </button>
          ))}
        </div>
        <RegisterTab
          // Remount per register so the drawer's form and staged
          // attachments never leak across tabs.
          key={complianceTab}
          config={REGISTER_CONFIGS[complianceTab]}
          contractorId={recordId}
          propertyId={activePropertyId}
          canManage={canManage}
          workerOptions={workers}
          onChanged={() => handleRegisterChanged(complianceTab)}
        />
      </>
    );
  };

  const stepRenderers = [
    renderBusinessDetails,
    renderContactDetails,
    renderContractScope,
    renderRiskClassification,
    renderComplianceRecords,
    renderReview,
  ];

  const canSubmit = useMemo(() => !!riskLevel, [riskLevel]);

  return (
    <LayoutWrapper>
      <ContractorPageWrapper>
        <Box className="contractor-box">
          <div className="ctr-header" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Button
                shape="circle"
                icon={<ArrowLeftOutlined />}
                onClick={() => history.push("/contractors")}
              />
              <div>
                <h2 className="ctr-title" style={{ fontSize: 22 }}>
                  {editId ? "Edit Contractor" : "Add New Contractor"}
                </h2>
                <div className="ctr-subtitle">{activeProperty?.name}</div>
              </div>
            </div>
            <div className="ctr-subtitle">Step {step + 1} of {STEPS.length}</div>
          </div>

          <div className="wizard-layout">
            <div className="wizard-sidebar">
              {STEPS.map((s, i) => (
                <div
                  key={s.title}
                  className={`wizard-step ${i === step ? "active" : ""} ${i < step ? "done" : ""}`}
                >
                  <div className="step-num">{i < step ? <CheckOutlined /> : i + 1}</div>
                  <div>
                    <div className="step-title">{s.title}</div>
                    <div className="step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
              <div className="wizard-progress">
                Progress
                <Progress percent={progress} showInfo={false} strokeColor="#0b2a1b" />
              </div>
            </div>

            <div className="wizard-content">
              {loadingExisting && (
                <div style={{ textAlign: "center", padding: "48px 0" }}><Spin size="large" /></div>
              )}
              {!loadingExisting && (
              <>
              <div className="wizard-section-title">{STEPS[step].title}</div>
              <div className="wizard-section-desc">{STEPS[step].desc}</div>
              <Form form={form} layout="vertical" scrollToFirstError>
                {stepRenderers[step]()}
              </Form>
              <div className="wizard-footer">
                <Button
                  size="large"
                  onClick={step === 0 ? () => history.push("/contractors") : back}
                >
                  {step === 0 ? "Cancel" : "← Back"}
                </Button>
                {step < STEPS.length - 1 ? (
                  <Button
                    size="large"
                    type="primary"
                    className="btn-dark-green"
                    onClick={next}
                    loading={stepSaving}
                    disabled={STEPS[step].key === "risk" && !riskLevel}
                  >
                    Continue →
                  </Button>
                ) : (
                  <Button
                    size="large"
                    type="primary"
                    className="btn-dark-green"
                    icon={<CheckOutlined />}
                    loading={saving}
                    disabled={!canSubmit}
                    onClick={submit}
                  >
                    {editId ? "Save Changes" : "Create Contractor Profile"}
                  </Button>
                )}
              </div>
              </>
              )}
            </div>
          </div>
        </Box>
      </ContractorPageWrapper>
    </LayoutWrapper>
  );
};

export default AddContractorWizard;
